import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { MutationRefetchQueries } from "~/common/types";
import type { EvidenceImporterDefinitionInput } from "~/common/types/graphql";
import {
  type GetEvidenceImporterDetailQuery,
  UpsertEvidenceImporterDocument,
  type UpsertEvidenceImporterMutation,
  type UpsertEvidenceImporterMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { translateError } from "~/lib/errors";

import { useDefinitionJsonManager } from "./useDefinitionJsonManager";

type UseEvidenceImporterFormProps = {
  importProjectId: string;
  dataSourceId: string;
  evidenceImporterId: string | null;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onSuccess?: () => void;
};

export const useEvidenceImporterForm = ({
  importProjectId,
  dataSourceId,
  evidenceImporterId,
  refetchQueriesOnEdit,
  onSuccess,
}: UseEvidenceImporterFormProps) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [name, setName] = useState("");

  const jsonManager = useDefinitionJsonManager({ serverDefinition: undefined });
  const { initFromServer, workingJson } = jsonManager;

  const [upsertEvidenceImporter] = useMutation<UpsertEvidenceImporterMutation, UpsertEvidenceImporterMutationVariables>(
    UpsertEvidenceImporterDocument,
    {
      refetchQueries: refetchQueriesOnEdit,
    },
  );

  const resetByEvidenceImporter = useCallback(
    (data: GetEvidenceImporterDetailQuery) => {
      const importer = data?.evidenceImporters?.[0];
      if (!importer) return;

      setName(importer.name);
      initFromServer((importer.definition ?? {}) as Record<string, unknown>);
    },
    [initFromServer],
  );

  const submit = useCallback(async () => {
    setProcessing(true);
    try {
      // `version` is server-managed and rejected by EvidenceImporterDefinitionInput.
      const { version: _, ...definitionWithoutVersion } = workingJson;

      const res = await upsertEvidenceImporter({
        variables: {
          input: {
            importProjectId,
            dataSourceId,
            id: evidenceImporterId ?? undefined,
            name,
            definition: definitionWithoutVersion as EvidenceImporterDefinitionInput,
          },
        },
      });

      const result = res.data?.cnutUpsertEvidenceImporter;
      if (result?.__typename === "ValidationError") {
        const fields = result.fields ?? [];
        const msg = fields.map((f) => `${f.code}: ${f.message}`).join(", ");
        toast.error(msg || "Validation error");
        return false;
      }

      if (evidenceImporterId) {
        toast.success(t("feature.common.updatedSuccessfully", { model: "evidenceImporter" }));
      } else {
        toast.success(t("feature.common.createdSuccessfully", { model: "evidenceImporter" }));
      }
      onSuccess?.();
      return true;
    } catch (e) {
      if (e instanceof Error) {
        toast.error(translateError(t, e));
      }
      return false;
    } finally {
      setProcessing(false);
    }
  }, [upsertEvidenceImporter, importProjectId, dataSourceId, evidenceImporterId, name, workingJson, t, onSuccess]);

  return {
    name,
    setName,
    resetByEvidenceImporter,
    submit,
    processing,
    jsonManager,
  };
};
